/**
 * BallPositionPanel
 *
 * Shows the authoritative ball position for a Catchball loop:
 *   - Which org unit currently holds the ball
 *   - What judgment prerequisites need to be satisfied before passing
 *   - Who has been involved (and how: session or delegate)
 *   - Actions: pass ball down, pass ball up, involve level below
 *
 * Data source: useCatchballTierTurnStore (DB-backed, §2.4 compliant).
 *
 * This panel surfaces the level-turn model (migration 224) alongside
 * the existing loop workbench. It reads from catchball_level_turns and
 * catchball_level_involvements directly.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Circle,
  CheckCircle2,
  Clock,
  Users,
  Send,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  Inbox,
  Eye,
  Play,
  GitBranch,
  MapPin,
  Merge,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCatchballTierTurnStore } from '../../../stores/prioritization/catchballTierTurnStore';
import { useCatchballBallStore, useBallsForLoop } from '../../../stores/prioritization/catchballBallStore';
import { useCatchballPathInstanceStore } from '../../../stores/prioritization/catchballPathInstanceStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useCatchballRouting } from '../../../hooks/prioritization/useCatchballRouting';
import {
  pathAnchorOrgUnitId,
  readDesignerPathParents,
  readDesignerPathSpine,
  resolveActingOrgUnitId,
  spineStopsAboveHolder,
  spineStopsBelowHolder,
} from '../../../lib/catchballPathSpine';
import {
  type CatchballTierTurn,
  type CatchballTierInvolvement,
  type InvolvementMode,
  type JudgmentPrerequisite,
  BALL_DIRECTION_LABELS,
  LEVEL_TURN_STATE_LABELS,
  INVOLVEMENT_MODE_LABELS,
  allRequiredPrereqsMet,
  unsatisfiedRequiredPrereqs,
} from '../../../types/prioritization/catchballTierTurn';
import { ballChildIsBlocking, type CatchballBall } from '../../../types/prioritization/catchballBall';
import { involvementText } from './handoff/threadLogic';
import { FanOutModal } from './FanOutModal';
import { AdjustPathModal } from './AdjustPathModal';
import { CatchballRoutingPolicyPanel } from './CatchballRoutingPolicyPanel';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';

/** Stable empty spine when neither track metadata nor path instance has one. */
const EMPTY_PATH_SPINE: string[] = [];
const EMPTY_CHILD_BALLS: CatchballBall[] = [];

// ============================================================================
// Props
// ============================================================================

interface BallPositionPanelProps {
  loopId: string;
  cycleId: string;
  /** Track this loop is bound to — used for routing policy inheritance + Path spine. */
  trackId?: string | null;
  /** The org unit the current user belongs to (for action gating) */
  userOrgUnitId?: string;
  /**
   * Org unit the actor may hold/pass as when they are not placed on the Path
   * (e.g. cycle owner kicking off at the executive anchor). When omitted, derived
   * from userOrgUnitId + Path spine + owner flag.
   */
  actingOrgUnitId?: string;
  /** Compact mode for embedding in tight layouts */
  compact?: boolean;
  /**
   * Default judgment prerequisites to attach when initiating or passing the ball.
   * Derived from the loop's track readiness (e.g. incomplete ranking sessions).
   */
  defaultJudgmentPrerequisites?: JudgmentPrerequisite[];
  /**
   * The org unit IDs this loop was originally directed to (from loop.intent.recipientOrgUnitIds).
   * When set, "pass down" and "involve" options are scoped to only these branches
   * so unrelated parts of the org chart don't appear as valid destinations.
   * Overridden by Path Instance remaining_spine when present.
   */
  recipientOrgUnitIds?: string[];
  /**
   * Name of the person currently holding the ball on the loop
   * (catchball_loops.ball_holder_user_id, resolved by the parent page).
   * Null/undefined = the ball is unclaimed. Loop possession — not the
   * level-turn state this panel otherwise renders — is the source of truth
   * for "who has the ball".
   */
  possessionHolderName?: string | null;
  /** Org-unit/tier context for the possession chip (e.g. the holder tier's node label). */
  possessionOrgLabel?: string | null;
  /** True when the current user is cycle owner or has process.edit. */
  isCycleOwnerOrEditor?: boolean;
  /**
   * When false, Pass/Fork are disabled even if the ball sits at the actor's org
   * stop — used so “Take the ball” (personal claim) stays a required step.
   * Defaults to true for legacy call sites.
   */
  canActAsHolder?: boolean;
}

// ============================================================================
// Involve Modal (lightweight inline form)
// ============================================================================

function InvolveModal({
  holderOrgUnitId,
  orgUnits,
  onSubmit,
  onClose,
}: {
  holderOrgUnitId: string;
  orgUnits: Array<{ id: string; name: string }>;
  onSubmit: (targetId: string, mode: InvolvementMode, requestContent?: string) => void;
  onClose: () => void;
}) {
  const [targetId, setTargetId] = useState('');
  const [mode, setMode] = useState<InvolvementMode>('session');
  const [requestContent, setRequestContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const eligible = orgUnits.filter(u => u.id !== holderOrgUnitId);

  const handleSubmit = async () => {
    if (!targetId) return;
    setSubmitting(true);
    await onSubmit(targetId, mode, mode === 'delegate' ? requestContent : undefined);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] w-full max-w-md mx-4 p-6 space-y-4">
        <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Involve a level in your judgment work
        </h3>

        <div className="space-y-1">
          <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Which level?</label>
          <select
            value={targetId}
            onChange={e => setTargetId(e.target.value)}
            className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a level…</option>
            {eligible.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">How?</label>
          <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Involvement mode">
            {(['session', 'delegate'] as InvolvementMode[]).map(m => {
              const selected = mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setMode(m)}
                  className={`relative flex gap-2.5  border-2 p-3 text-left transition-all ${
                    selected
                      ? m === 'session'
                        ? 'border-primary-500 bg-primary-50 shadow-sm ring-2 ring-primary-500/20 dark:bg-primary-950/30 dark:ring-primary-400/25'
                        : 'border-[var(--slate)] bg-[var(--slate-dim)] shadow-sm ring-2 ring-violet-500/20 dark:bg-[var(--slate)]/30 dark:ring-violet-400/25'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:hover:border-[var(--rule-bright)]'
                  }`}
                >
                  {selected && (
                    <CheckCircle2
                      className={`absolute right-2 top-2 h-4 w-4 shrink-0 ${
                        m === 'session'
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                      }`}
                      aria-hidden
                    />
                  )}
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center  ${
                      selected
                        ? m === 'session'
                          ? 'bg-primary-100 dark:bg-primary-900/50'
                          : 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/50'
                        : 'bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)]'
                    }`}
                  >
                    {m === 'session' ? (
                      <Users
                        className={`h-4 w-4 ${
                          selected
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                        }`}
                      />
                    ) : (
                      <Send
                        className={`h-4 w-4 ${
                          selected
                            ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                            : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                        }`}
                      />
                    )}
                  </div>
                  <div className="min-w-0 pr-5">
                    <div
                      className={`text-sm font-semibold capitalize ${
                        selected
                          ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
                          : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'
                      }`}
                    >
                      {m}
                    </div>
                    <div
                      className={`mt-0.5 text-xs font-normal leading-snug ${
                        selected
                          ? 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'
                          : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                      }`}
                    >
                      {m === 'session' ? 'Participate in your sessions' : 'Deliver a specific output'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {mode === 'delegate' && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              What output do you need from them?
            </label>
            <textarea
              value={requestContent}
              onChange={e => setRequestContent(e.target.value)}
              rows={3}
              placeholder="Describe the specific output you need this level to deliver…"
              className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!targetId || submitting || (mode === 'delegate' && !requestContent.trim())}
            className="px-4 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Involving…' : 'Involve level'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Pass Ball Modal
// ============================================================================

function PassBallModal({
  direction,
  orgUnits,
  fromOrgUnitId,
  onSubmit,
  onClose,
}: {
  direction: 'down' | 'up';
  orgUnits: Array<{ id: string; name: string }>;
  fromOrgUnitId: string;
  onSubmit: (toOrgUnitId: string, content?: string) => void;
  onClose: () => void;
}) {
  const [toOrgUnitId, setToOrgUnitId] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const eligible = orgUnits.filter(u => u.id !== fromOrgUnitId);

  const handleSubmit = async () => {
    if (!toOrgUnitId) return;
    setSubmitting(true);
    await onSubmit(toOrgUnitId, content || undefined);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
        <h3 className="text-base font-semibold text-[var(--text-pri)] flex items-center gap-2">
          {direction === 'down'
            ? <><ArrowDown className="h-4 w-4 text-[var(--text-ter)]" /> Pass ball down</>
            : <><ArrowUp className="h-4 w-4 text-[var(--green)]" /> Pass ball up (propose + approve)</>
          }
        </h3>

        <div className="space-y-1">
          <label className="text-xs font-medium text-[var(--text-sec)]">
            {direction === 'down' ? 'Which level receives the request?' : 'Which level receives your proposal?'}
          </label>
          <select
            value={toOrgUnitId}
            onChange={e => setToOrgUnitId(e.target.value)}
            className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full text-sm border border-[var(--rule)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">Select a level…</option>
            {eligible.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-[var(--text-sec)]">
            {direction === 'down' ? 'Request / intent (optional)' : 'Proposal summary (optional)'}
          </label>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={3}
            placeholder={direction === 'down'
              ? 'What are you asking this level to figure out?'
              : 'Summary of your proposal and rationale…'
            }
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!toOrgUnitId || submitting}
            className={`px-4 py-2 text-sm font-medium  text-white disabled:opacity-50 transition-colors ${
              direction === 'down'
                ? 'bg-[var(--slate)] hover:bg-[var(--slate)]'
                : 'bg-[var(--green)] hover:bg-[var(--green)]'
            }`}
          >
            {submitting ? 'Passing…' : direction === 'down' ? 'Pass down' : 'Pass up'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Prereq row
// ============================================================================

function PrereqRow({
  prereq,
  satisfaction,
}: {
  prereq: JudgmentPrerequisite;
  satisfaction?: { satisfied: boolean; satisfiedAt?: string; satisfiedBy?: string };
}) {
  const satisfied = satisfaction?.satisfied ?? false;
  return (
    <div className="flex items-start gap-3 py-2">
      {satisfied
        ? <CheckCircle2 className="h-4 w-4 text-[var(--green)] shrink-0 mt-0.5" />
        : prereq.required
          ? <Circle className="h-4 w-4 text-[var(--mustard)] shrink-0 mt-0.5" />
          : <Circle className="h-4 w-4 text-[var(--text-ter)] shrink-0 mt-0.5" />
      }
      <div className="flex-1 min-w-0">
        <span className={`text-sm ${satisfied ? 'line-through text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
          {prereq.label}
        </span>
        {prereq.required && !satisfied && (
          <span className="ml-2 text-xs text-[var(--mustard)] font-medium">Required</span>
        )}
        {satisfied && satisfaction?.satisfiedAt && (
          <span className="ml-2 text-xs text-[var(--text-ter)]">
            {new Date(satisfaction.satisfiedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Involvement row
// ============================================================================

function InvolvementRow({
  involvement,
  orgUnitName,
}: {
  involvement: CatchballTierInvolvement;
  orgUnitName?: string;
}) {
  const isDelegate = involvement.involvementMode === 'delegate';
  const delivered = involvement.state === 'delivered';

  return (
    <div className="flex items-start gap-3 py-2">
      <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
        isDelegate
          ? delivered ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]' : 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]'
          : 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]'
      }`}>
        {isDelegate
          ? delivered
            ? <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] dark:text-[var(--text-ter)]" />
            : <Send className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          : <Users className="h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[var(--text-pri)]">
            {orgUnitName ?? involvement.targetOrgUnitId}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
            isDelegate
              ? delivered
                ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/50 dark:text-[var(--text-ter)]'
                : 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/50'
              : 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/50'
          }`}>
            {INVOLVEMENT_MODE_LABELS[involvement.involvementMode]}
          </span>
          {isDelegate && !delivered && (
            <span className="text-xs text-[var(--mustard)]">Awaiting output</span>
          )}
          {isDelegate && delivered && (
            <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">Output delivered</span>
          )}
        </div>
        {isDelegate && involvement.requestContent && (
          <p className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-2">
            {typeof involvement.requestContent === 'string'
              ? involvement.requestContent
              : (involvement.requestContent as { description?: string }).description ?? ''}
          </p>
        )}
        {/* The response itself — "Output delivered" alone told the holder
            something came back but never what. */}
        {isDelegate && delivered && involvementText(involvement.deliveredContent) && (
          <p className="mt-1.5 border-l-2 border-[var(--green)] bg-[var(--surface)] px-2.5 py-1.5 text-xs text-[var(--text-sec)] whitespace-pre-wrap">
            {involvementText(involvement.deliveredContent)}
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function BallPositionPanel({
  loopId,
  cycleId,
  trackId,
  userOrgUnitId,
  actingOrgUnitId: actingOrgUnitIdProp,
  compact = false,
  defaultJudgmentPrerequisites,
  recipientOrgUnitIds,
  possessionHolderName,
  possessionOrgLabel,
  isCycleOwnerOrEditor = false,
  canActAsHolder = true,
}: BallPositionPanelProps) {
  const loadLoop           = useCatchballTierTurnStore(s => s.loadLoop);
  const getLoopState       = useCatchballTierTurnStore(s => s.getLoopState);
  const getActivePositions = useCatchballTierTurnStore(s => s.getActivePositions);
  const isLoadingStore     = useCatchballTierTurnStore(s => s.isLoading);
  const getError           = useCatchballTierTurnStore(s => s.getError);
  const initiateTurn       = useCatchballTierTurnStore(s => s.initiateTurn);
  const passBall           = useCatchballTierTurnStore(s => s.passBall);
  const involveLevel       = useCatchballTierTurnStore(s => s.involveLevel);
  const fanOut             = useCatchballTierTurnStore(s => s.fanOut);
  const originateLoop      = useCatchballTierTurnStore(s => s.originateLoop);
  const aggregateBalls     = useCatchballTierTurnStore(s => s.aggregateBalls);

  const fetchBallsForLoop = useCatchballBallStore(s => s.fetchBallsForLoop);
  const ballsById = useCatchballBallStore(s => s.ballsById);
  // Contract §2.4: must not crash the holder surface — use the stable hook,
  // never `getBallsForLoop` inside a Zustand selector.
  const ballsForLoop = useBallsForLoop(loopId);

  const pathInstance = useCatchballPathInstanceStore(s => s.byLoop[loopId] ?? null);
  const fetchPathInstance  = useCatchballPathInstanceStore(s => s.fetchForLoop);
  const ensurePathInstance = useCatchballPathInstanceStore(s => s.ensurePathInstance);
  const adjustPathInstance = useCatchballPathInstanceStore(s => s.adjustPathInstance);

  const tracks      = useTrackStore(s => s.tracks);
  const orgUnitsMap = useEnterpriseIdentityStore(s => s.orgUnits);
  const orgUnits = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  const userId      = useUserStore(s => s.userId);
  const can         = useUserStore(s => s.can);
  const { routing, breakdown } = useCatchballRouting(cycleId, trackId);

  const [showInvolveModal, setShowInvolveModal]   = useState(false);
  const [showPassDownModal, setShowPassDownModal] = useState(false);
  const [showPassUpModal, setShowPassUpModal]     = useState(false);
  const [showFanOutModal, setShowFanOutModal]     = useState(false);
  const [showAdjustPathModal, setShowAdjustPathModal] = useState(false);
  const [aggregating, setAggregating]             = useState(false);
  const [initiating, setInitiating]               = useState(false);
  const [actionError, setActionError]             = useState<string | null>(null);

  useEffect(() => {
    loadLoop(loopId);
    fetchBallsForLoop(loopId);
    void fetchPathInstance(loopId);
  }, [loopId, loadLoop, fetchBallsForLoop, fetchPathInstance]);

  const pathSpine = useMemo(() => {
    const track = trackId ? tracks.find((t) => t.id === trackId) : undefined;
    const fromTrack = readDesignerPathSpine(
      (track?.metadata ?? null) as Record<string, unknown> | null,
    );
    // Prefer the designer Path (anchor = executives). A Path Instance may have
    // been seeded from recipients alone; never treat that as the executive spine.
    if (fromTrack.length > 0) return fromTrack;
    if (pathInstance?.spineOrgUnitIds?.length) return pathInstance.spineOrgUnitIds;
    return EMPTY_PATH_SPINE;
  }, [pathInstance, trackId, tracks]);

  // A path may fork; the parents map says which branch each stop is on.
  const pathParents = useMemo(() => {
    const track = trackId ? tracks.find((t) => t.id === trackId) : undefined;
    return readDesignerPathParents((track?.metadata ?? null) as Record<string, unknown> | null);
  }, [trackId, tracks]);

  const pathAnchorId = pathAnchorOrgUnitId(pathSpine);

  useEffect(() => {
    if (!userId || pathInstance) return;
    const track = trackId ? tracks.find((t) => t.id === trackId) : undefined;
    const spine = readDesignerPathSpine(
      (track?.metadata ?? null) as Record<string, unknown> | null,
    );
    const ensureSpine = spine.length > 0 ? spine : (recipientOrgUnitIds ?? []);
    if (ensureSpine.length === 0) return;
    void ensurePathInstance({
      loopId,
      spineOrgUnitIds: ensureSpine,
      pathSource: 'track_metadata',
      actorId: userId,
    });
  }, [userId, pathInstance, trackId, tracks, recipientOrgUnitIds, loopId, ensurePathInstance]);

  const loading       = isLoadingStore(loopId);
  const loadError     = getError(loopId);
  const loopState     = getLoopState(loopId);
  const activePositions = getActivePositions(loopId);

  const userOrgUnitIds = useEnterpriseIdentityStore((s) =>
    userId ? s.getUserOrgUnitIds(userId) : s.getUserOrgUnitIds(''),
  );

  const actingOrgUnitId =
    actingOrgUnitIdProp ??
    resolveActingOrgUnitId({
      userOrgUnitId,
      userOrgUnitIds,
      pathSpine,
      activeHolderOrgUnitId: activePositions[0]?.ballHolderOrgUnitId ?? null,
      isCycleOwnerOrEditor,
    });

  // Prefer the branch at the actor's effective org unit when the rollout has siblings.
  const activePos =
    (actingOrgUnitId
      ? activePositions.find((p) => p.ballHolderOrgUnitId === actingOrgUnitId)
      : undefined) ??
    (userOrgUnitId
      ? activePositions.find((p) => p.ballHolderOrgUnitId === userOrgUnitId)
      : undefined) ??
    activePositions[0] ??
    null;

  const activeTurn: CatchballTierTurn | null = loopState?.turns.find(
    t => t.id === activePos?.turnId
  ) ?? null;

  const activeInvolvements: CatchballTierInvolvement[] = loopState?.involvements.filter(
    inv => activeTurn && inv.tierTurnId === activeTurn.id && inv.state !== 'cancelled'
  ) ?? [];

  const holderOrgUnit = orgUnits.find(u => u.id === activePos?.ballHolderOrgUnitId);
  const userIsHolder =
    canActAsHolder &&
    !!actingOrgUnitId &&
    activePos?.ballHolderOrgUnitId === actingOrgUnitId;

  const parentBallId = useMemo(() => {
    if (activePos?.ballId) return activePos.ballId;
    if (activeTurn?.ballId) return activeTurn.ballId;
    if (!activePos) return null;
    const atHolder = ballsForLoop.find(
      (b) => b.recipientOrgUnitId === activePos.ballHolderOrgUnitId && b.ballStatus !== 'closed',
    );
    if (atHolder) return atHolder.id;
    return ballsForLoop.find((b) => b.parentBallId == null)?.id ?? null;
  }, [activePos, activeTurn, ballsForLoop]);

  const childBalls = useMemo(() => {
    if (!parentBallId) return EMPTY_CHILD_BALLS;
    return Object.values(ballsById)
      .filter((b) => b.parentBallId === parentBallId)
      .sort((a, b) => a.sequenceInLoop - b.sequenceInLoop);
  }, [parentBallId, ballsById]);
  const blockingChildren = childBalls.filter(ballChildIsBlocking);
  const childrenBlockPassUp = blockingChildren.length > 0;
  const parentBall = parentBallId
    ? ballsForLoop.find((b) => b.id === parentBallId)
    : undefined;
  const needsAggregate =
    routing.requireAggregateBeforePassUp &&
    childBalls.length > 0 &&
    !parentBall?.rollupAggregatedAt &&
    !childrenBlockPassUp;

  // Server allows pass-up from active|ready_to_pass when required prereqs are met
  // (including the empty-prereq case). UI must not require ready_to_pass alone —
  // turns can stay `active` until a mark-satisfied transition, even with 0 prereqs
  // if they were inserted that way.
  const prereqsMetForPassUp = activeTurn ? allRequiredPrereqsMet(activeTurn) : false;
  const canPassUp =
    !!userIsHolder &&
    !!activeTurn &&
    (activeTurn.state === 'ready_to_pass' ||
      (activeTurn.state === 'active' && prereqsMetForPassUp)) &&
    !childrenBlockPassUp &&
    !needsAggregate;
  const canPassDown = userIsHolder && activeTurn?.state !== 'passed' && activeTurn?.state !== 'returned';
  const canInvolve  = userIsHolder && activeTurn && (activeTurn.state === 'active' || activeTurn.state === 'ready_to_pass');
  const canFanOut =
    !!userIsHolder &&
    !!activeTurn &&
    routing.rolloutEnabled &&
    routing.ballSplitUnit !== 'disabled' &&
    can('catchball.rollout') &&
    (activeTurn.state === 'active' || activeTurn.state === 'ready_to_pass') &&
    activePos?.ballDirection !== 'up' &&
    childBalls.length < routing.ballSplitMaxChildren;
  const canAggregate =
    !!userIsHolder && needsAggregate && can('catchball.aggregate');
  const canAdjustPath =
    !!userIsHolder &&
    can('catchball.adjust_path') &&
    routing.pathAdjustPolicy !== 'none' &&
    (routing.pathAdjustPolicy === 'holder_remaining_spine' ||
      (routing.pathAdjustPolicy === 'cycle_owner_only' && isCycleOwnerOrEditor));

  const effectiveRecipientScope =
    pathInstance && pathInstance.remainingSpine.length > 0
      ? pathInstance.remainingSpine
      : pathSpine.length > 0
        ? pathSpine
        : (recipientOrgUnitIds ?? []);

  // Prefer ordered Path stops below the current holder (executives → next level).
  // Org-descendant ∩ spine is a fallback for loops without a designer Path.
  const passDownOptions = (() => {
    const holderId = activePos?.ballHolderOrgUnitId;
    if (!holderId) return [];

    if (pathSpine.length > 0) {
      return spineStopsBelowHolder(pathSpine, holderId, pathParents).map((id) => ({
        id,
        name: orgUnits.find((u) => u.id === id)?.name ?? id,
      }));
    }

    const configuredIds = new Set(effectiveRecipientScope);
    return orgUnits
      .filter(u => {
        if (!u.path.includes(holderId)) return false;
        if (configuredIds.size > 0) {
          const inBranch = u.id !== holderId && (
            configuredIds.has(u.id) ||
            u.path.some(id => configuredIds.has(id)) ||
            orgUnits.some(r => configuredIds.has(r.id) && r.path.includes(u.id))
          );
          if (!inBranch) return false;
        }
        return true;
      })
      .map(u => ({ id: u.id, name: u.name }));
  })();

  // "Pass up" options: Path stops above the holder, else org ancestors
  const passUpOptions = (() => {
    const holderId = activePos?.ballHolderOrgUnitId;
    if (pathSpine.length > 0 && holderId) {
      return spineStopsAboveHolder(pathSpine, holderId, pathParents).map((id) => ({
        id,
        name: orgUnits.find((u) => u.id === id)?.name ?? id,
      }));
    }
    if (!holderOrgUnit) return [];
    const ancestorIds = new Set(holderOrgUnit.path);
    return orgUnits
      .filter(u => ancestorIds.has(u.id))
      .map(u => ({ id: u.id, name: u.name }));
  })();

  // "Involve" options: descendants within configured branches (same scoping as pass down)
  // excluding the holder itself
  const involveOptions = passDownOptions.filter(u => u.id !== activePos?.ballHolderOrgUnitId);

  const handlePassDown = async (toOrgUnitId: string, content?: string) => {
    if (!activePos || !userId) return;
    setActionError(null);
    const result = await passBall({
      loopId,
      fromOrgUnitId:  activePos.ballHolderOrgUnitId,
      toOrgUnitId,
      passDirection:  'down',
      content:        content ? { description: content } : undefined,
      actorId:        userId,
      cycleId,
    });
    if (!result.success) setActionError(result.error ?? 'Failed to pass ball');
    else setShowPassDownModal(false);
  };

  const handlePassUp = async (toOrgUnitId: string, content?: string) => {
    if (!activePos || !userId) return;
    setActionError(null);
    const result = await passBall({
      loopId,
      fromOrgUnitId:  activePos.ballHolderOrgUnitId,
      toOrgUnitId,
      passDirection:  'up',
      content:        content ? { description: content } : undefined,
      actorId:        userId,
      cycleId,
    });
    if (!result.success) setActionError(result.error ?? 'Failed to pass ball');
    else setShowPassUpModal(false);
  };

  // Executives (Path anchor) start with the ball — never the designer's personal
  // placement above the chart.
  const initiateOrgUnitId = pathAnchorId ?? actingOrgUnitId ?? userOrgUnitId;
  const canInitiate =
    !!userId &&
    !!initiateOrgUnitId &&
    (isCycleOwnerOrEditor ||
      userOrgUnitId === initiateOrgUnitId ||
      actingOrgUnitId === initiateOrgUnitId);

  const handleInitiate = async () => {
    if (!initiateOrgUnitId || !userId || !canInitiate) return;
    setInitiating(true);
    setActionError(null);
    const result = await initiateTurn({
      loopId,
      orgUnitId: initiateOrgUnitId,
      actorId:   userId,
      judgmentPrerequisites: defaultJudgmentPrerequisites?.length
        ? defaultJudgmentPrerequisites
        : undefined,
    });
    setInitiating(false);
    if (!result.success) setActionError(result.error ?? 'Failed to initiate turn');
  };

  const handleInvolve = async (targetId: string, mode: InvolvementMode, requestContent?: string) => {
    if (!activePos || !userId) return;
    setActionError(null);
    const result = await involveLevel({
      loopId,
      holderOrgUnitId: activePos.ballHolderOrgUnitId,
      targetOrgUnitId: targetId,
      involvementMode: mode,
      requestContent:  requestContent ? { description: requestContent } : undefined,
      actorId:         userId,
    });
    if (!result.success) setActionError(result.error ?? 'Failed to involve level');
    else setShowInvolveModal(false);
  };

  const resolveParentBallId = async (): Promise<string | null> => {
    if (parentBallId) return parentBallId;
    if (!activePos || !userId) return null;
    const originated = await originateLoop({
      loopId,
      originatorOrgUnitId: activePos.ballHolderOrgUnitId,
      originatorOrgUnitName: holderOrgUnit?.name,
      requestPayload: activeTurn?.receivedContent ?? undefined,
      actorId: userId,
    });
    if (!originated.success) {
      setActionError(originated.error ?? 'Could not create a root ball for this loop');
      return null;
    }
    return originated.ballId ?? null;
  };

  const handleFanOut = async (
    targets: Array<{ id: string; name: string }>,
    requestText?: string,
  ) => {
    if (!activePos || !userId) return;
    setActionError(null);
    const parentId = await resolveParentBallId();
    if (!parentId) throw new Error('No parent ball available to split from');

    // One server transaction (catchball_fan_out, migration 756): every target
    // is validated first, and a refusal on any of them sends none.
    const result = await fanOut({
      loopId,
      parentBallId: parentId,
      targets: targets.map((target) => ({
        recipientOrgUnitId: target.id,
        recipientOrgUnitName: target.name,
        requestPayload: {
          request_text: requestText ?? `Request for ${target.name}`,
          ...(activeTurn?.receivedContent ?? {}),
        },
      })),
    });

    if (!result.success) {
      const detail = result.invalidTargets?.length
        ? result.invalidTargets.map((t) => `${t.target}: ${t.reason}`).join('; ')
        : result.error ?? 'refused';
      setActionError(`Nothing was sent — ${detail}`);
      throw new Error(result.error ?? detail);
    }
    setShowFanOutModal(false);
  };

  const handleAggregate = async () => {
    if (!parentBallId || !userId) return;
    setAggregating(true);
    setActionError(null);
    const result = await aggregateBalls({
      parentBallId,
      actorId: userId,
      mergedContent: {
        aggregated_from_child_count: childBalls.length,
        at: new Date().toISOString(),
      },
    });
    setAggregating(false);
    if (!result.success) setActionError(result.error ?? 'Failed to aggregate');
  };

  const handleAdjustPath = async (remainingOrgUnitIds: string[]) => {
    if (!userId) return;
    setActionError(null);
    const result = await adjustPathInstance({
      loopId,
      ballId: parentBallId,
      remainingOrgUnitIds,
      mode: 'rewrite_remaining',
      actorId: userId,
    });
    if (!result.success) throw new Error(result.error ?? 'Failed to adjust path');
    setShowAdjustPathModal(false);
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-[var(--text-ter)] text-sm">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Loading ball position…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
        <AlertCircle className="h-4 w-4 shrink-0" />
        {loadError}
      </div>
    );
  }

  // ── No turns yet ──────────────────────────────────────────────────────────
  if (!activePos || !activeTurn) {
    const anchorLabel =
      (pathAnchorId && orgUnits.find((u) => u.id === pathAnchorId)?.name) ||
      'the executive level';
    return (
      <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)] px-5 py-6 text-center space-y-3">
        <Inbox className="h-6 w-6 text-[var(--text-ter)] mx-auto" />
        <div>
          <p className="text-sm text-[var(--text-sec)] font-medium">No active turn yet</p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            {pathAnchorId
              ? `${anchorLabel} starts with the ball. Start this level's turn here, then pass down to the next Path level.`
              : "Start this level's turn, then pass it on."}
          </p>
        </div>

        {actionError && (
          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--coral-dim)] border border-[var(--coral)] text-xs text-[var(--coral)] text-left">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {actionError}
          </div>
        )}

        {canInitiate && (
          <button
            onClick={handleInitiate}
            disabled={initiating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
          >
            <Play className="h-3.5 w-3.5" />
            {initiating ? 'Initiating…' : CATCHBALL_LABELS.startTurn}
          </button>
        )}
      </div>
    );
  }

  const unmetRequired = unsatisfiedRequiredPrereqs(activeTurn);
  const allMet        = allRequiredPrereqsMet(activeTurn);

  return (
    <>
      {/* Modals */}
      {showInvolveModal && holderOrgUnit && (
        <InvolveModal
          holderOrgUnitId={activePos.ballHolderOrgUnitId}
          orgUnits={involveOptions}
          onSubmit={handleInvolve}
          onClose={() => setShowInvolveModal(false)}
        />
      )}
      {showPassDownModal && (
        <PassBallModal
          direction="down"
          orgUnits={passDownOptions}
          fromOrgUnitId={activePos.ballHolderOrgUnitId}
          onSubmit={handlePassDown}
          onClose={() => setShowPassDownModal(false)}
        />
      )}
      {showPassUpModal && (
        <PassBallModal
          direction="up"
          orgUnits={passUpOptions}
          fromOrgUnitId={activePos.ballHolderOrgUnitId}
          onSubmit={handlePassUp}
          onClose={() => setShowPassUpModal(false)}
        />
      )}
      {showFanOutModal && (
        <FanOutModal
          orgUnits={involveOptions}
          alreadyDelegatedIds={childBalls
            .filter(ballChildIsBlocking)
            .map((b) => b.recipientOrgUnitId)
            .filter((id): id is string => !!id)}
          maxChildren={routing.ballSplitMaxChildren}
          onSubmit={handleFanOut}
          onClose={() => setShowFanOutModal(false)}
        />
      )}
      {showAdjustPathModal && (
        <AdjustPathModal
          remainingSpine={pathInstance?.remainingSpine ?? effectiveRecipientScope}
          plannedSpine={pathInstance?.spineOrgUnitIds ?? []}
          orgUnits={orgUnits.map((u) => ({ id: u.id, name: u.name }))}
          onSubmit={handleAdjustPath}
          onClose={() => setShowAdjustPathModal(false)}
        />
      )}

      <div className="space-y-4">

        {/* Action error */}
        {actionError && (
          <div className="flex items-center gap-2 px-4 py-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {actionError}
            <button onClick={() => setActionError(null)} className="ml-auto text-[var(--coral)] hover:text-[var(--coral)]">×</button>
          </div>
        )}

        {!compact && <CatchballRoutingPolicyPanel breakdown={breakdown} compact />}

        {/* Rollout child-gate — server also enforces this on pass up */}
        {childrenBlockPassUp && (
          <div className="flex items-start gap-2 px-4 py-3 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]/50 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
            <GitBranch className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Rollup blocked — waiting on child balls</p>
              <p className="text-xs mt-0.5 opacity-90">
                {blockingChildren.length} of {childBalls.length} delegated{' '}
                {childBalls.length === 1 ? 'ball has' : 'balls have'} not returned an accepted
                proposal yet. Combine their responses before passing up.
              </p>
            </div>
          </div>
        )}

        {needsAggregate && !childrenBlockPassUp && (
          <div className="flex items-start gap-2 px-4 py-3 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)]/50 text-sm text-[var(--text-sec)]">
            <Merge className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{CATCHBALL_LABELS.combineResponses} required before pass-up</p>
              <p className="text-xs mt-0.5 opacity-90">
                Child balls are resolved. Merge their proposals at this Split Node to unlock pass-up.
              </p>
            </div>
          </div>
        )}

        {/* Ball position header */}
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
          {/* Level-turn trail label + loop-possession chip (the only custody signal) */}
          <div className="px-4 pt-3 pb-1 flex items-center justify-between">
            <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
              Turn history
            </p>
            <span className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {possessionHolderName ? `Ball with ${possessionHolderName}` : 'Ball unclaimed'}
              {(possessionOrgLabel ?? holderOrgUnit?.name) && (
                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] font-normal">
                  {' '}— {possessionOrgLabel ?? holderOrgUnit?.name}
                </span>
              )}
            </span>
          </div>

          {/* Direction indicator */}
          <div className={`px-4 py-2 text-xs font-medium flex items-center gap-2 ${
            activePos.ballDirection === 'up'
              ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/30 text-[var(--green)] dark:text-[var(--text-ter)] border-b border-[var(--green)] dark:border-[var(--green)]/50'
              : activePos.ballDirection === 'down'
                ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border-b border-[var(--slate)] dark:border-[var(--slate)]/50'
                : 'bg-[var(--surface)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] border-b border-[var(--rule)] dark:border-[var(--rule)]'
          }`}>
            {activePos.ballDirection === 'down' && <ArrowDown className="h-3.5 w-3.5" />}
            {activePos.ballDirection === 'up'   && <ArrowUp className="h-3.5 w-3.5" />}
            {BALL_DIRECTION_LABELS[activePos.ballDirection]}
            <span className="ml-auto flex items-center gap-3">
              <span className="text-xs opacity-70">Turn #{activePos.sequenceNumber}</span>
              {activePos.ballDirection === 'up' && (
                <Link
                  to={`/prioritization/cascade/inbox/${cycleId}`}
                  className="font-semibold underline underline-offset-2 hover:opacity-80"
                >
                  Review proposal →
                </Link>
              )}
            </span>
          </div>

          {/* Current holder */}
          <div className="px-4 py-4 flex items-center gap-4">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
              allMet ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/40' : 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40'
            }`}>
              <span className="text-xl">⚾</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                  {holderOrgUnit?.name ?? activePos.ballHolderOrgUnitId}
                </p>
                {userIsHolder && (
                  <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/50 px-2 py-0.5 rounded-full font-medium">
                    You
                  </span>
                )}
              </div>
              <p className={`text-xs mt-0.5 font-medium ${
                allMet ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'
              }`}>
                {LEVEL_TURN_STATE_LABELS[activeTurn.state]}
                {!allMet && unmetRequired.length > 0 && (
                  <span className="text-[var(--text-ter)] font-normal ml-1">
                    — {unmetRequired.length} prerequisite{unmetRequired.length > 1 ? 's' : ''} remaining
                  </span>
                )}
              </p>
            </div>
            {/* Received timestamp */}
            <div className="shrink-0 text-right">
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">Received</p>
              <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {new Date(activeTurn.receivedAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Actions */}
          {userIsHolder && !compact && (
            <div className="px-4 pb-4 flex flex-wrap gap-2">
              <button
                onClick={() => setShowPassDownModal(true)}
                disabled={!canPassDown}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/50 border border-[var(--slate)] dark:border-[var(--slate)]/50 disabled:opacity-40 transition-colors"
              >
                <ArrowDown className="h-3.5 w-3.5" />
                Pass down
              </button>

              {routing.rolloutEnabled && (
                <button
                  onClick={() => setShowFanOutModal(true)}
                  disabled={!canFanOut || involveOptions.length === 0}
                  title={
                    !can('catchball.rollout')
                      ? 'You do not have catchball.rollout permission'
                      : involveOptions.length === 0
                        ? 'No descendant teams available to fan out to'
                        : 'Split this ball into parallel child balls (Rollout)'
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/50 border border-[var(--slate)] dark:border-[var(--slate)]/50 disabled:opacity-40 transition-colors"
                >
                  <GitBranch className="h-3.5 w-3.5" />
                  Fan out
                  {childBalls.length > 0 && (
                    <span className="text-xs opacity-75">({childBalls.length})</span>
                  )}
                </button>
              )}

              {canAggregate && (
                <button
                  type="button"
                  onClick={() => void handleAggregate()}
                  disabled={aggregating}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/50 border border-[var(--slate)] dark:border-[var(--slate)]/50 disabled:opacity-40 transition-colors"
                >
                  <Merge className="h-3.5 w-3.5" />
                  {aggregating ? 'Combining…' : CATCHBALL_LABELS.combineResponses}
                </button>
              )}

              {canAdjustPath && (
                <button
                  type="button"
                  onClick={() => setShowAdjustPathModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--mustard)] dark:text-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 hover:bg-[var(--mustard-dim)] dark:hover:bg-[var(--mustard)]/50 border border-[var(--mustard)] dark:border-[var(--mustard)]/50 transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  Adjust path
                </button>
              )}

              <button
                onClick={() => setShowInvolveModal(true)}
                disabled={!canInvolve}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/50 border border-[var(--slate)] dark:border-[var(--slate)]/50 disabled:opacity-40 transition-colors"
              >
                <Users className="h-3.5 w-3.5" />
                Involve level
              </button>

              <button
                onClick={() => setShowPassUpModal(true)}
                disabled={!canPassUp}
                title={
                  childrenBlockPassUp
                    ? 'Waiting for child balls to return accepted proposals'
                    : needsAggregate
                      ? `${CATCHBALL_LABELS.combineResponses} before passing up`
                      : !canPassUp
                        ? 'Complete required judgment work before passing up'
                        : 'Pass the ball up to the next Path level'
                }
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] dark:bg-[var(--green)]/30 hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/50 border border-[var(--green)] dark:border-[var(--green)]/50 disabled:opacity-40 transition-colors"
              >
                <ArrowUp className="h-3.5 w-3.5" />
                {CATCHBALL_LABELS.passUp}
              </button>
            </div>
          )}
        </div>

        {/* Active rollout children under this ball */}
        {!compact && childBalls.length > 0 && (
          <div className="border border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)]/50 dark:bg-[var(--slate)]/10 overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--slate)] dark:border-[var(--slate)]/40 flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Rollout — child balls
              </span>
              <span className="ml-auto text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                {childBalls.length - blockingChildren.length}/{childBalls.length} ready to roll up
              </span>
            </div>
            <div className="divide-y divide-sky-100/80 dark:divide-sky-800/30">
              {childBalls.map((ball) => {
                const blocking = ballChildIsBlocking(ball);
                return (
                  <div
                    key={ball.id}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                        {ball.recipientOrgUnitName ?? ball.name}
                      </p>
                      <p className="text-xs text-[var(--text-ter)] truncate">{ball.name}</p>
                    </div>
                    <span
                      className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                        blocking
                          ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]'
                          : 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]'
                      }`}
                    >
                      {blocking ? 'In flight' : 'Resolved'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Prerequisites */}
        {!compact && activeTurn.judgmentPrerequisites.length > 0 && (
          <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
            <div className="px-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Judgment work</span>
              {allMet && (
                <span className="ml-auto text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> All complete
                </span>
              )}
            </div>
            <div className="px-4 divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {activeTurn.judgmentPrerequisites.map(prereq => (
                <PrereqRow
                  key={prereq.code}
                  prereq={prereq}
                  satisfaction={activeTurn.prerequisiteSatisfaction[prereq.code]}
                />
              ))}
            </div>
          </div>
        )}

        {/* Involvements — rendered only when levels have actually been involved.
            It used to draw a full bordered panel whose entire content was a
            sentence saying it was empty, on every ball. The "Involve level"
            button above is the entry point; it does not need a panel to
            announce its own absence. */}
        {!compact && activeInvolvements.length > 0 && (
          <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
            <div className="px-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] flex items-center gap-2">
              <Eye className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                Involved levels
              </span>
              <span className="ml-auto text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                {activeInvolvements.length} level{activeInvolvements.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="px-4 divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {activeInvolvements.map(inv => (
                <InvolvementRow
                  key={inv.id}
                  involvement={inv}
                  orgUnitName={orgUnits.find(u => u.id === inv.targetOrgUnitId)?.name}
                />
              ))}
            </div>
          </div>
        )}

        {/* Recent turns (collapsed) */}
        {!compact && loopState && loopState.turns.filter(t => t.state === 'passed').length > 0 && (
          <details className="group">
            <summary className="flex items-center gap-2 text-xs text-[var(--text-ter)] cursor-pointer hover:text-[var(--text-sec)] select-none">
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
              {loopState.turns.filter(t => t.state === 'passed').length} completed turn{loopState.turns.filter(t => t.state === 'passed').length > 1 ? 's' : ''}
            </summary>
            <div className="mt-2 space-y-1">
              {loopState.turns
                .filter(t => t.state === 'passed')
                .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
                .map(turn => (
                  <div key={turn.id} className="flex items-center gap-3 px-3 py-2 bg-[var(--surface)] text-xs text-[var(--text-ter)]">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
                    <span className="font-medium text-[var(--text-sec)]">
                      {orgUnits.find(u => u.id === turn.orgUnitId)?.name ?? turn.orgUnitId}
                    </span>
                    <span className="text-[var(--text-ter)]">
                      {turn.ballDirection === 'down' ? '↓ request' : turn.ballDirection === 'up' ? '↑ proposal' : 'initiated'}
                    </span>
                    <span className="ml-auto">
                      {turn.passedAt ? new Date(turn.passedAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                ))}
            </div>
          </details>
        )}
      </div>
    </>
  );
}

export default BallPositionPanel;
